import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompositeScan } from '@models/meter-scans/composite/composite-scan';
import { DropData } from '@models/meter-scans/drop-data';
import { HowlerData } from '@models/meter-scans/howler-data';
import { IngressData } from '@models/meter-scans/ingress-data';
import { NoiseData } from '@models/meter-scans/noise-data';
import { OCCData } from '@models/meter-scans/occ-data';
import { OfdmaData } from '@models/meter-scans/ofdma/ofdma-data';
import { OSAData } from '@models/meter-scans/osa-data';
import { OTDRData } from '@models/meter-scans/otdr-data';
import { PingData } from '@models/meter-scans/ping-data';
import { QOTData } from '@models/meter-scans/qot-data';
import { SFPData } from '@models/meter-scans/sfp-data';
import { SpeedTestData } from '@models/meter-scans/speed-test-data';
import { UsAlignmentData } from '@models/meter-scans/us-alignment-data';
import { OfdmData } from '@models/ofdm-data';
import { Privileges } from '@models/privileges';
import { ReleaseNotes } from '@models/release-notes/release-notes';
import { TechProfile } from '@models/tech-profile';
import { UserPreferences } from '@models/user-preferences';
import { UserProfile } from '@models/user-profile';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObjectHelper } from '@utilities/object-helper';
import {ItuSearchData} from '@models/meter-scans/itu-search-data';
import { HeatmapData } from '@models/heatmap-data';


@Injectable()
export class WebAppService {
  DEFAULT_CHANNEL_WIDTH = 6.4;
  constructor(private http: HttpClient) {
  }

  esriLoggedIn = false;

  getReleaseNotes(): Observable<ReleaseNotes> {
    const URL = '/web-app-svc/notifications/dashboard-release-notes';
    return this.http.get<ReleaseNotes>(URL);
  }

  getUploadedPhotoData(start?: number, limit?: number): Observable<any> {
    const URL = `/web-app-svc/photo/list?start=${start}&limit=${limit}`;
    return this.http.get<any>(URL);
  }

  getTechCompositeDataDetail(targetUser: string, ts: number): Observable<CompositeScan> {
    const URL = `/web-app-svc/tech/compositeDetail?user=${targetUser}&ts=${ts}`;
    return this.http.get<any>(URL).pipe(
      map((response: any) => {
        const scan = CompositeScan.getScanFromHttpResponse(response);
        scan.equalizationData.map(eqData => {
          if (!eqData.bandwidth) {
            eqData.bandwidth = this.DEFAULT_CHANNEL_WIDTH;
          }
        });
        return scan;
      }),
    );
  }

  getHowlerData(targetUser: string, ts: number): Observable<HowlerData> {
    const URL = `/web-app-svc/tech/howlerDetail?user=${targetUser}&ts=${ts}`;
    return this.http.get<HowlerData>(URL).pipe(
      map((howlerData: HowlerData) => new HowlerData(howlerData))
    );
  }

  getIngressData(targetUser: string, ts: number): Observable<IngressData> {
    const URL = `/web-app-svc/tech/ingressDetail?user=${targetUser}&ts=${ts}`;
    return this.http.get<IngressData>(URL).pipe(
      map((ingressData: IngressData) => new IngressData(ingressData))
    );
  }

  getNoiseData(username: string, timestamp: number): Observable<NoiseData> {
    const URL = `web-app-svc/tech/noiseDetail?user=${username}&ts=${timestamp}`;
    return this.http.get<NoiseData>(URL).pipe(
      map((noiseData: NoiseData) => new NoiseData(noiseData))
    );
  }

  getOTDRData(username: string, timestamp: number): Observable<OTDRData> {
    const URL = `web-app-svc/xmfh/otdr-detail?user=${username}&ts=${timestamp}`;
    return this.http.get<OTDRData>(URL).pipe(
      map((otdrData: OTDRData) => new OTDRData(otdrData))
    );
  }

  getITUSearchData(username: string, timestamp: number): Observable<ItuSearchData > {
    const URL = `web-app-svc/xmfh/itu-search?user=${username}&ts=${timestamp}`;
    return this.http.get<ItuSearchData>(URL).pipe(
      map((data: ItuSearchData) => new ItuSearchData(data))
    );
  }

  getOSAData(username: string, timestamp: number): Observable<OSAData> {
    const URL = `web-app-svc/xmfh/osa-detail?user=${username}&ts=${timestamp}`;
    return this.http.get<any>(URL).pipe(
      map((osaData: any) => ObjectHelper.copyObject(osaData, OSAData))
    );
  }

  getOCCData(username: string, timestamp: number): Observable<OCCData> {
    //const URL = 'web-app-svc/xmfh/occ-detail?user=smaffe776&ts=1662577815655';
    const URL = `web-app-svc/xmfh/occ-detail?user=${username}&ts=${timestamp}`;

    return this.http.get<any>(URL).pipe(
      map((occData: any) => this.copyObject(occData)));

  }

  getHeatMapData(username: string, timestamp: number): Observable<HeatmapData> {
    const URL = `web-app-svc/v2/tech/heat-map?user=${username}&ts=${timestamp}`;
    return this.http.get<HeatmapData>(URL).pipe(
      map((heatMapData: HeatmapData) => ObjectHelper.copyObject(heatMapData, HeatmapData))
    );
  }

  copyObject(occData: any) {
    let data: OCCData = {
      latestTenGEponDownstream: occData ? occData[1577] : 0,
      latestBauDWDM: occData ? occData[1550] : 0,
      latestTenGEponUpstream: occData ? occData[1270] : 0,
      latestAllOtherBands: occData ? occData[4] : 0,
      tenGEponDownstream: [],
      bauDWDM: [],
      tenGEponUpstream: [],
      allOtherBands: [],
      timestamps: []
    }
    return data;
  }
 getSFPData(username: string, timestamp: number) : Observable<SFPData>  {
    const URL = `web-app-svc/xmfh/sfp-detail?user=${username}&ts=${timestamp}`;
    return this.http.get<SFPData>(URL).pipe(
      map((sfpdata) => ObjectHelper.copyObject(sfpdata, SFPData))
    );
  }
  getOFDMAData(targetUser: string, ts: number): Observable<OfdmaData> {
    const URL = `/web-app-svc/tech/ofdma-detail?user=${targetUser}&ts=${ts}`;
    return this.http.get<OfdmaData>(URL).pipe(
      map((ofdmaData) => ObjectHelper.copyObject(ofdmaData, OfdmaData))
    );
  }

  getSavedOFDM(username: string, timestamp: number): Observable<OfdmData> {
    let params = new HttpParams();
    params = params.append('user', username);
    params = params.append('ts', timestamp.toString());

    const URL = '/web-app-svc/tech/ofdmDetail';
    return this.http.get<OfdmData>(URL, { params: params }).pipe(
      map((ofdmData: OfdmData) => new OfdmData(ofdmData))
    );
  }

  getUsAlignmentData(targetUser: string, ts: number): Observable<UsAlignmentData> {
    const URL = `/web-app-svc/tech/usAlignmentDetail?user=${targetUser}&ts=${ts}`;
    return this.http.get(URL).pipe(
      map(usAlignmentData => {
        return ObjectHelper.copyObject(usAlignmentData, UsAlignmentData);
      }),
      map(usAlignmentData => {
        usAlignmentData.init();
        return usAlignmentData;
      })
    );
  }
  getUsAlignmentsData(scanPayload: {username: string, timestamp: number}[]): Observable<UsAlignmentData> {
    const URL = `/web-app-svc/tech/usAlignmentDetails`;

    return this.http.post(URL, scanPayload).pipe(
      map(usAlignmentData => {
        return ObjectHelper.copyObject(usAlignmentData, UsAlignmentData);
      }),
      map(usAlignmentData => {
        usAlignmentData.init();
        return usAlignmentData;
      })
    );
  }
  getQOTData(username: string, ts: string | number): Observable<QOTData> {
    const URL = `/web-app-svc/tech/qotDetail?user=${username}&ts=${ts}`;
    return this.http.get<QOTData>(URL).pipe(
      map((qotData: QOTData) => new QOTData(qotData))
    );
  }

  getPingData(username: string, ts: string | number): Observable<PingData> {
    const URL = `web-app-svc/tech/ping-detail?user=${username}&ts=${ts}`;
    return this.http.get<PingData>(URL).pipe(
      map((pingData: PingData) => new PingData(pingData))
    );
  }

  getSpeedTestData(targetUser: string, ts: string | number): Observable<SpeedTestData> {
    const URL = `/web-app-svc/tech/speedTestDetail?user=${targetUser}&ts=${ts}`;
    return this.http.get<SpeedTestData>(URL).pipe(
      map((speedTestData: SpeedTestData) => new SpeedTestData(speedTestData))
    );
  }

  privileges(): Observable<Privileges> {
    const URL = '/web-app-svc/tech/privileges';
    return this.http.get<Privileges>(URL);
  }

  privilegeAdd(username: string, privilegeId: number): Observable<Privileges> {
    const URL = `/web-app-svc/tech/privilege-add?user=${username}&id=${privilegeId}`;
    return this.http.post<any>(URL, {});
  }

  privilegeRemove(username: string, privilege: number) {
    const URL = `/web-app-svc/tech/privilege-remove?user=${username}&id=${privilege}`;
    return this.http.delete<any>(URL);
  }

  profile(username?: string): Observable<UserProfile> {
    let url = '/web-app-svc/tech/profile';
    if (username) {
      url += '/' + username;
    }
    return this.http.get<UserProfile>(url);
  }

  profileImageUpload(data: any) {
    const URL = '/web-app-svc/tech/profileImage';
    return this.http.post(URL, data, {});
  }

  preferencesGet(): Observable<UserPreferences> {
    const URL = '/web-app-svc/tech/profilePreferencesByUser';
    return this.http.get(URL).pipe(
      map((userPrefData) => ObjectHelper.copyObject(userPrefData, UserPreferences))
    );
  }

  preferencesPut(prefs: UserPreferences): Observable<UserPreferences> {
    const URL = '/web-app-svc/tech/profilePreferencesByUser';
    return this.http.put(URL, prefs).pipe(
      map((userPrefData) => ObjectHelper.copyObject(userPrefData, UserPreferences))
    );
  }

  postMobileAppProfile(username: string, mobileAppProfile: string): Observable<any> {
    const URL = '/web-app-svc/tech/updateAppProfile';
    return this.http.post<any>(URL, {
      usernames: [username],
      profile: mobileAppProfile
    });
  }

  userTypePut(userType: string | null = null, partnerId: string | null = null): Observable<TechProfile> {
    const URL = `/web-app-svc/tech/updateUserType?type=${userType}&partnerid=${partnerId}`;
    return this.http.put<any>(URL, {});
  }

  getJobPhotoByUsernameAndTimestamp(targetUser: string, ts: number): Observable<Blob> {
    const URL = `/web-app-svc/tech/work-order-photo?user=${targetUser}&ts=${ts}`;
    return this.http.get(URL, { responseType: 'blob' });
  }

  ownerExemptGet(username: string): Observable<boolean> {
    const URL = `/web-app-svc/tech/xm_owner_exempt/${username}`;
    return this.http.get<boolean>(URL);
  }

  ownerExemptPut(username: string, isExempt: boolean): Observable<boolean> {
    const URL = `/web-app-svc/tech/xm_owner_exempt/${username}?exempt=${isExempt}`;
    return this.http.put<boolean>(URL, {});
  }

  getDropValidationData(username: string, timestamp: number | string) {
    const URL = `/web-app-svc/tech/drop-validation-detail?user=${username}&ts=${timestamp}`;
    return this.http.get<DropData>(URL);
  }

  /**
     *
     * This API call is used to test if the user has an active authentication token to be used with all GIS layer requests
     */
  getEsriLayer() {
    const url = 'https://aegis-enterprise.cable.comcast.com/server/rest/services/CC_SNET_HFCNetwork/CC_SNET_NODE_SITE/MapServer?f=json';
    return this.http.get(url);
  }
}
